import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concat, filter, from, map, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { SurveyActivityLogActions } from '@ps/survey-data-access';
import { SURVEY_ACTIVITY_LOG_MESSAGES } from '@ps/survey-utils';

import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from './survey-creation-audience.actions';
import { selectPendingHighCpiConfirmations } from './survey-creation-audience.selectors';
import { selectSurveyId } from '../core/survey-creation-core.selectors';

@Injectable()
export class SurveyCreationConfirmationEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  public readonly logHighCpiConfirmation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.confirmHighCpi),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      map(([{ cpi }, surveyId]) =>
        surveyId
          ? SurveyActivityLogActions.logActivity({
              log: {
                eventSource: 'user',
                message: SURVEY_ACTIVITY_LOG_MESSAGES.confirmedHighCpi,
                surveyId,
                metadata: { cpi },
              },
            })
          : SurveyCreationAudienceActions.queueHighCpiConfirmation({ cpi }),
      ),
    ),
  );

  /**
   * Flush point. Every path that first produces a surveyId is listened to, so a
   * confirmation made before the draft existed is never lost
   */
  public readonly flushQueuedHighCpiConfirmations$ = createEffect(() =>
    this._actions$.pipe(
      // Both creation paths produce the first surveyId, so both have to flush —
      // listening only to the standard one stranded a confirmation made before a
      // survey created from a template, logging it never and clearing it never.
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
      ),
      concatLatestFrom(() =>
        this._store.select(selectPendingHighCpiConfirmations),
      ),
      filter(([, pending]) => pending.length > 0),
      switchMap(([{ survey }, pending]) =>
        concat(
          from(pending).pipe(
            map((cpi) =>
              SurveyActivityLogActions.logActivity({
                log: {
                  eventSource: 'user',
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.confirmedHighCpi,
                  surveyId: survey.id,
                  metadata: { cpi },
                },
              }),
            ),
          ),
          of(SurveyCreationAudienceActions.clearHighCpiConfirmationQueue()),
        ),
      ),
    ),
  );
}
